import { useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  DEVICE_TROUBLESHOOT_SESSIONS_QUERY,
  DEVICE_TROUBLESHOOT_TRANSCRIPT_QUERY,
  RESUME_DEVICE_TROUBLESHOOT_SESSION,
  START_DEVICE_TROUBLESHOOT_SESSION,
} from '@scio3/graphql-client';
import { useOptibotStore } from '../stores/optibot-store';
import {
  ConnState,
  SessionStatus,
  type DeviceSessionOutput,
  type SessionEvent,
} from './useDeviceSessionStream';

/** A history-list row. Mirrors open-api's DeviceTroubleshootSession ObjectType. */
export interface DeviceTroubleshootSessionSummary {
  sessionId: string;
  deviceUUID: string;
  deviceId: string;
  deviceName?: string | null;
  title?: string | null;
  teamId?: string | null;
  actor: string;
  mode: string; // 'customer' | 'staff'
  status: string; // 'creating' | 'running' | 'ended' | 'failed' | 'paused'
  region: string;
  startedAt: string;
  endedAt?: string | null;
  threadId?: string | null; // OptiBot conversation thread → chat-history card rebuild
}

/** The account's device-troubleshoot sessions (read-only history), optionally scoped to one device.
 *  A session's `status` is a server snapshot. There is NO realtime subscription, so a cached list goes
 *  stale the instant a session ends elsewhere (e.g. ended in the MDM drawer while the OptiBot list is open) —
 *  and a stale `running` drives a wrong live/history decision (a reconnect could even boot a phantom session).
 *  So we ALWAYS fetch fresh from the server (`network-only`) — the list is the authoritative status, no cache
 *  to go stale. `refetch` is exposed for an explicit refresh. */
export function useDeviceTroubleshootSessions(deviceUuid?: string) {
  const { data, loading, error, refetch } = useQuery<{
    deviceTroubleshootSessions: DeviceTroubleshootSessionSummary[];
  }>(DEVICE_TROUBLESHOOT_SESSIONS_QUERY, {
    variables: { deviceUuid },
    fetchPolicy: 'network-only',
  });
  const sessions = data?.deviceTroubleshootSessions ?? [];

  // Reconcile the in-chat card badge: it reads the store's live conn (set by the session view's SSE), which
  // goes stale once that view unmounts. On each (re)load, find any tracked session that's now terminal and
  // correct its stale conn in memory so the card flips to Ended/Error too — not just the list.
  const setSessionConn = useOptibotStore((s) => s.setSessionConn);
  const sessionConn = useOptibotStore((s) => s.sessionConn);
  useEffect(() => {
    for (const s of sessions) {
      const live = sessionConn[s.sessionId];
      if (!live || live === ConnState.Ended || live === ConnState.Error) continue;
      if (s.status === SessionStatus.Ended) setSessionConn(s.sessionId, ConnState.Ended);
      else if (s.status === SessionStatus.Failed) setSessionConn(s.sessionId, ConnState.Error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sessionConn, setSessionConn]);

  return { sessions, loading, error, refetch };
}

/** The recorded transcript for one session (skips until a session is selected). The JSON-scalar payload
 *  already matches SessionEvent, so it feeds the view as `initialEvents` for the read-only replay.
 *  `network-only`: a session opened while still booting (live-but-empty) would otherwise cache an EMPTY
 *  transcript, and cache-and-network could serve that stale-empty on reopen — leaving the view stuck on
 *  "Connecting to the device…" for a session that has since recorded activity. Always fetch fresh. */
export function useDeviceTroubleshootTranscript(sessionId: string | null) {
  const { data, loading, error } = useQuery<{
    deviceTroubleshootSessionTranscript: SessionEvent[];
  }>(DEVICE_TROUBLESHOOT_TRANSCRIPT_QUERY, {
    variables: { sessionId },
    skip: !sessionId,
    fetchPolicy: 'network-only',
  });
  return { events: data?.deviceTroubleshootSessionTranscript ?? [], loading, error };
}

/** Resume → device-agent-api boots a fresh runner + re-mints a live capability token. Returns the live
 *  handle the view opens its SSE against. Throws are surfaced via the returned `error`; callers try/catch. */
export function useResumeDeviceTroubleshootSession() {
  const [mutate, { loading, error }] = useMutation<{
    resumeDeviceTroubleshootSession: DeviceSessionOutput;
  }>(RESUME_DEVICE_TROUBLESHOOT_SESSION);

  const resume = async (
    sessionId: string,
    deviceUuid: string,
  ): Promise<DeviceSessionOutput | null> => {
    const res = await mutate({ variables: { sessionId, deviceUuid } });
    return res.data?.resumeDeviceTroubleshootSession ?? null;
  };
  return { resume, loading, error };
}

/** Start a NEW troubleshoot session for a device (the "Start session" button) → device-agent-api boots
 *  a fresh runner. Same gates as the OptiBot path (server-side). Returns the live handle to open the SSE. */
export function useStartDeviceTroubleshootSession() {
  const [mutate, { loading, error }] = useMutation<{
    startDeviceTroubleshootSession: DeviceSessionOutput;
  }>(START_DEVICE_TROUBLESHOOT_SESSION);

  const start = async (
    deviceId: string,
    task?: string,
    title?: string,
  ): Promise<DeviceSessionOutput | null> => {
    const res = await mutate({ variables: { deviceId, task, title } });
    return res.data?.startDeviceTroubleshootSession ?? null;
  };
  return { start, loading, error };
}
